import Circle from "@arcgis/core/geometry/Circle";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import Graphic from "@arcgis/core/Graphic";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import MapView from "@arcgis/core/views/MapView";
import { computeTrueNorth, getMapView } from "../../../utils/arcgisUtils";
import type GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import type { SceneViewGraphicHit } from "@arcgis/core/views/types";

export const overettHandlerPointerMove = (
    view: MapView,
    overettLinjeRef: React.RefObject<Graphic | null> | null | undefined
) => {
    const overettOverlayLayer = view.map?.findLayerById("overettOverlayLayer") as GraphicsLayer;

    return view.on("pointer-move", (event) => {
        view.hitTest(event, { include: overettOverlayLayer }).then((response) => {
            if (!overettLinjeRef?.current || !view.container) return;

            if (
                !response.results ||
                response.results.length === 0 ||
                (response.results[0] as SceneViewGraphicHit).graphic.geometry?.type === "polyline"
            ) {
                view.container.style.cursor = "default";
            } else {
                view.container.style.cursor = "move";
            }
        });
    });
};

export const overettHandlerDrag = (
    view: MapView,
    setOverettLinje: (value: Graphic | null) => void,
    isDraggingOverettLinjeRef: React.RefObject<boolean>,
    setOverettAngle: (angle: number) => void,
    setOverettObserver: (point: Point | null) => void
) => {
    const overettOverlayLayer = view.map?.findLayerById("overettOverlayLayer") as GraphicsLayer;

    return view.on("drag", async (event) => {
        if (!view.container) return;
        if (view.container.style.cursor !== "move" && !isDraggingOverettLinjeRef.current) return;

        if (event.action === "end") {
            isDraggingOverettLinjeRef.current = false;
            view.container.style.cursor = "default";

            const lineGraphic = overettOverlayLayer?.graphics.find((graphic) => graphic.geometry?.type === "polyline");
            if (!lineGraphic) return;

            const endPoint = view.toMap({ x: event.x, y: event.y });

            const newLine = await updateOverettLinje(
                [(lineGraphic.geometry as Polyline).paths[0][0][0], (lineGraphic.geometry as Polyline).paths[0][0][1]],
                [endPoint.x, endPoint.y],
                setOverettAngle,
                setOverettObserver
            );

            if (!newLine) return;

            setOverettLinje(newLine);
            return;
        }

        isDraggingOverettLinjeRef.current = true;

        event.stopPropagation();

        const lineGraphic = overettOverlayLayer?.graphics.find((graphic) => graphic.geometry?.type === "polyline");
        if (!lineGraphic) return;

        const endPoint = view.toMap({ x: event.x, y: event.y });
        updateOverettLinje(
            [(lineGraphic.geometry as Polyline).paths[0][0][0], (lineGraphic.geometry as Polyline).paths[0][0][1]],
            [endPoint.x, endPoint.y],
            setOverettAngle
        );
    });
};

export const updateOverettLinje = async (
    startCoords: [number, number],
    endCoords: [number, number],
    setOverettAngle: (angle: number) => void,
    setOverettObserver?: (point: Point | null) => void,
    redraw: boolean = true
) => {
    const view = getMapView();
    if (!view) return;

    const overettOverlayLayer = view.map?.findLayerById("overettOverlayLayer") as GraphicsLayer;
    if (!overettOverlayLayer) return;

    const lineGraphic = overettOverlayLayer.graphics.find((graphic) => graphic.geometry?.type === "polyline");
    if (!lineGraphic) return;

    const newLine = lineGraphic.clone();

    const newLineGeometry = new Polyline({
        paths: [[startCoords, endCoords]],
        spatialReference: view.spatialReference,
    });

    newLine.geometry = newLineGeometry;

    if (redraw) {
        addOverlayGraphics(newLine);
    }

    const startPoint = new Point({
        x: startCoords[0],
        y: startCoords[1],
        spatialReference: view.spatialReference,
    });
    const trueNorth = await computeTrueNorth(startPoint);
    const rotationOffset = 270;

    const angle =
        (-Math.atan2(endCoords[1] - startCoords[1], endCoords[0] - startCoords[0]) * 180) / Math.PI +
        rotationOffset +
        trueNorth;

    setOverettAngle(angle % 360);
    setOverettObserver?.(
        new Point({
            x: endCoords[0],
            y: endCoords[1],
            spatialReference: view.spatialReference,
        })
    );

    return newLine;
};

export const addOverlayGraphics = async (overettLinjeGraphic: Graphic) => {
    const view = getMapView();
    if (!view || !overettLinjeGraphic) return;

    const overlayLinjeGraphicToAdd = overettLinjeGraphic.clone();

    const overlayGraphicsLayer = view.map?.findLayerById("overettOverlayLayer") as GraphicsLayer;
    if (!overlayGraphicsLayer) return;

    overlayGraphicsLayer.removeAll();

    const dragHandleGraphic = new Graphic({
        geometry: new Circle({
            center: new Point({
                x: (overettLinjeGraphic.geometry as Polyline).paths[0][1][0],
                y: (overettLinjeGraphic.geometry as Polyline).paths[0][1][1],
                spatialReference: view.spatialReference,
            }),
            radius: 10,
            spatialReference: view.spatialReference,
            geodesic: false,
        }),
        symbol: new SimpleMarkerSymbol({
            style: "circle",
            color: [0, 0, 0, 1],
            size: 10,
        }),
        attributes: {
            objectType: "dragHandle",
        },
    });

    overlayGraphicsLayer.addMany([overlayLinjeGraphicToAdd, dragHandleGraphic]);
};
